import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import { type ButtonHTMLAttributes, forwardRef } from "react";
import { cn } from "../lib/cn";

/**
 * Button — the reference primitive. Heights follow the control scale
 * (26 / 28 / 32px), radius 6px, weight 500, 12–13px type.
 */
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-sm font-medium transition-colors " +
    "disabled:pointer-events-none disabled:opacity-50 select-none " +
    "focus-visible:outline-2 focus-visible:outline-ring focus-visible:outline-offset-2",
  {
    variants: {
      variant: {
        primary: "bg-accent text-fg-on-accent hover:bg-accent-hover",
        secondary:
          "bg-surface text-fg border border-border hover:bg-hover hover:border-border-strong",
        ghost: "text-fg-muted hover:bg-surface-inset hover:text-fg",
        danger: "bg-surface text-err-fg border border-err-border hover:bg-err-subtle",
        link: "text-accent hover:text-accent-hover underline-offset-2 hover:underline h-auto px-0",
      },
      size: {
        sm: "h-[26px] px-2 text-sm",
        md: "h-[28px] px-2.5 text-sm",
        lg: "h-[32px] px-3 text-base",
        icon: "h-[32px] w-[32px] p-0",
      },
    },
    defaultVariants: { variant: "secondary", size: "lg" },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /** Render the child element instead of a <button> (for links). */
  asChild?: boolean;
  /** Show a spinner and disable interaction. */
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, size, asChild = false, loading = false, children, disabled, ...props },
    ref,
  ) => {
    // Slot needs exactly one child, so the spinner is only rendered on a real <button>.
    if (asChild) {
      return (
        <Slot
          ref={ref}
          className={cn(buttonVariants({ variant, size }), className)}
          aria-busy={loading || undefined}
          {...props}
        >
          {children}
        </Slot>
      );
    }
    return (
      <button
        ref={ref}
        className={cn(buttonVariants({ variant, size }), className)}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        {...props}
      >
        {loading ? <Loader2 className="size-3.5 animate-spin" aria-hidden /> : null}
        {children}
      </button>
    );
  },
);
Button.displayName = "Button";

export { buttonVariants };
